import { FC, MouseEvent, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { useGetOtherUsersQuery, useGetUserDetailsQuery } from "@store/services/user";

import { getInitials } from "@utils";
import { getSwitchUserUrl } from "./AccountMenu.utilities";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SwitchAccountOutlinedIcon from "@mui/icons-material/SwitchAccountOutlined";

import { brandColorTokens, logoutUrl } from "@constants";
import { applicationSpaRoutes } from "@shared/constants/routes";
import { headerActionButtonStyle } from "../../Header.styles";
import { headerMenuOrigins } from "../../Header.constants";

const AccountMenu: FC = () => {
  const { t } = useTranslation();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const { data: userDetails } = useGetUserDetailsQuery();
  const { data: otherUsers } = useGetOtherUsersQuery();

  const isMenuOpen = anchorElement !== null;

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => setAnchorElement(event.currentTarget);

  const handleMenuClose = () => setAnchorElement(null);

  return (
    <>
      <Button
        color="inherit"
        aria-label={t("appHeader.userMenu.myProfile")}
        onClick={handleMenuOpen}
        endIcon={
          <ExpandMoreIcon sx={{ transition: "transform 0.2s", transform: isMenuOpen ? "rotate(180deg)" : "none" }} />
        }
        data-cy="user-menu-button"
        data-amp-default-track={true}
        data-amp-event-type="header.button.userMenuExpand"
        title={userDetails?.fullname}
        data-amp-mask={true}
        sx={[
          headerActionButtonStyle,
          {
            width: "auto",
            minWidth: 0,
            flexShrink: 1,
            gap: 1,
            pl: 0.75,
            pr: { xs: 0.75, sm: 1 },
            "& .MuiButton-endIcon": { ml: 0, display: { xs: "none", sm: "inherit" } },
          },
        ]}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: brandColorTokens.text.displayInverted,
            color: brandColorTokens.text.display,
            typography: "body2",
            fontWeight: 500,
          }}
        >
          {userDetails?.fullname ? getInitials(userDetails.fullname) : <PersonOutlineIcon fontSize="small" />}
        </Avatar>
        <Box
          component="span"
          sx={{
            display: { xs: "none", sm: "block" },
            minWidth: 0,
            maxWidth: { sm: 120, md: 200 },
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 500,
          }}
        >
          {userDetails?.fullname}
        </Box>
      </Button>
      <Menu
        anchorEl={anchorElement}
        open={isMenuOpen}
        onClose={handleMenuClose}
        {...headerMenuOrigins}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
        data-cy="user-menu-list"
      >
        {userDetails && (
          <Box sx={{ px: 2, pt: 1, pb: 1.5, maxWidth: 280 }} data-amp-mask={true}>
            <Typography variant="subtitle1" sx={{ color: brandColorTokens.text.display, overflowWrap: "anywhere" }}>
              {userDetails.fullname}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>
              {userDetails.email}
            </Typography>
          </Box>
        )}
        {userDetails && <Divider />}
        <MenuItem
          component={RouterLink}
          to={applicationSpaRoutes.profileEdit}
          onClick={handleMenuClose}
          data-amp-default-track={true}
          data-amp-event-type="header.link.userMenuEditProfile"
        >
          <ListItemIcon>
            <PersonOutlineIcon fontSize="small" />
          </ListItemIcon>
          {t("appHeader.userMenu.myProfile")}
        </MenuItem>
        {otherUsers?.map((otherUser) => (
          <MenuItem
            key={otherUser.id}
            component="a"
            href={getSwitchUserUrl(otherUser.id, window.location.href)}
            data-amp-default-track={true}
            data-amp-event-type="header.link.userMenuSwitchUser"
            data-amp-mask={true}
          >
            <ListItemIcon>
              <SwitchAccountOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {otherUser.fullname}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          component="a"
          href={logoutUrl}
          data-amp-default-track={true}
          data-amp-event-type="header.link.userMenuLogout"
        >
          <ListItemIcon>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {t("appHeader.userMenu.logOut")}
        </MenuItem>
      </Menu>
    </>
  );
};

export default AccountMenu;
